// ProposalItems.jsx

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

// Context
import { useBase } from 'context/BaseContext';
import { useCurrentLanguage } from 'context/LanguageContext';
import { useNodeTypes } from 'context/NodeTypesContext';
import { useUser } from 'context/UserContext';

// Services
import { moveNodeToTrash, updateNode } from 'services/nodes/NodeWriteService';
import { resolveImageUrl } from 'services/storage/StorageAccessService';

// Utilities
import { formatPrice } from 'utilities/Currency';
import { getOptionLabel, resolveEnumOptionsForField } from 'utilities/EnumOptions';
import { buildNodeFieldPatch } from 'utilities/NodeFieldData';
import { buildNodeSubtitlePatch } from 'utilities/NodeSubtitle';

// Local
import AddProposalItemFromProductModal from 'nodetypes/proposal/fromProduct/AddProposalItemFromProductModal';
import {
  PROPOSAL_ITEM_TYPE_KEY,
  PROPOSAL_TYPE_KEY,
  readProposalField,
} from 'nodetypes/proposal/view/proposalGeneralInfoModel';
import DocumentItemsColumnHeader from 'nodetypes/documents/kit/DocumentItemsColumnHeader';
import {
  DEFAULT_PROPOSAL_CLIENT_VISIBLE_COLUMNS,
  DEFAULT_PROPOSAL_ITEMS_DISPLAY_PREFS,
  isProposalColumnClientVisible,
  normalizeProposalClientVisibleColumns,
  PROPOSAL_CLIENT_TOGGLEABLE_COLUMN_KEYS,
  readProposalItemsDisplayPrefs,
  writeProposalItemsDisplayPrefs,
} from 'nodetypes/proposal/view/proposalItemsDisplayPrefs';
import {
  DOCUMENT_PAYMENT_TYPE_KEY,
} from 'nodetypes/documents/payments/documentPaymentKeys';
import {
  augmentProposalItemPricingPatch,
  computeProposalItemsRollups,
  getProposalItemRowModel,
  readProposalItemField,
} from 'nodetypes/proposal/view/proposalItemsModel';
import ProposalItemsSettingsModal from 'nodetypes/proposal/view/ProposalItemsSettingsModal';
import {
  InlineMoneyCell,
  InlineNumberCell,
  InlineSelectCell,
  InlineTextCell,
} from 'nodetypes/purchaseorder/view/PurchaseOrderItemsInlineCells';
import useNodelistTargetOptions from 'node/form/fields/nodelist/useNodelistTargetOptions';

// CSS
import 'nodetypes/purchaseorder/view/PurchaseOrderItems.css';
import './ProposalItems.css';

// Components
import Button from 'components/button/Button';
import Checkbox from 'components/checkbox/Checkbox';
import Confirm from 'components/confirm/Confirm';
import EnumPillSelect from 'components/enumpillselect/EnumPillSelect';
import IconButton from 'components/iconbutton/IconButton';
import Switch from 'components/switch/Switch';

function cleanString(value) {
  return String(value || '').trim();
}

function formatMoneyCell(value) {
  if (value === null || value === undefined || value === '') {
    return <span className="purchase-order-items-muted">—</span>;
  }
  return formatPrice(value);
}

function formatMarkupCell(markupAmount, markupPercent) {
  const amountText = markupAmount == null ? '' : formatPrice(markupAmount);
  const percentText = markupPercent == null ? '' : `${markupPercent}%`;
  if (!amountText && !percentText) {
    return <span className="purchase-order-items-muted">—</span>;
  }
  if (amountText && percentText) return `${amountText} (${percentText})`;
  return amountText || percentText;
}

function getItemSchemaField(itemType, fieldName) {
  const fields = Array.isArray(itemType?.schema?.fields) ? itemType.schema.fields : [];
  return fields.find((field) => cleanString(field?.name) === fieldName) || null;
}

const ItemThumb = React.memo(({ itemKey, coverUrl }) => {
  const [src, setSrc] = useState(coverUrl || '');

  useEffect(() => {
    let cancelled = false;
    const raw = cleanString(coverUrl);
    if (!raw) {
      setSrc('');
      return undefined;
    }
    if (
      raw.startsWith('blob:')
      || raw.startsWith('data:')
      || raw.startsWith('http://')
      || raw.startsWith('https://')
      || raw.startsWith('/')
    ) {
      setSrc(raw);
      return undefined;
    }
    (async () => {
      try {
        const signed = await resolveImageUrl({ nodeKey: itemKey || null, url: raw });
        if (!cancelled) setSrc(signed || raw);
      } catch {
        if (!cancelled) setSrc(raw);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [coverUrl, itemKey]);

  return (
    <div className="purchase-order-items-thumb" aria-hidden="true">
      {src ? (
        <img src={src} alt="" draggable="false" loading="lazy" />
      ) : null}
    </div>
  );
});

ItemThumb.displayName = 'ItemThumb';

/**
 * ProposalItems
 *
 * Houzz-inspired editable items table for PROPOSAL.
 */
const ProposalItems = React.memo(({
  node,
  nodeType = null,
  readOnly = false,
  neighborNodes = [],
  onRelatedNodeOpen = null,
}) => {
  const { activeBase } = useBase();
  const { user } = useUser();
  const { getType } = useNodeTypes();
  const language = useCurrentLanguage();

  const [addOpen, setAddOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [displayPrefs, setDisplayPrefs] = useState(DEFAULT_PROPOSAL_ITEMS_DISPLAY_PREFS);
  // Document-level client column visibility (optimistic + synced from node)
  const [clientVisibleColumns, setClientVisibleColumns] = useState(
    () => DEFAULT_PROPOSAL_CLIENT_VISIBLE_COLUMNS,
  );
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);
  const [removeBusy, setRemoveBusy] = useState(false);

  const baseKey = cleanString(activeBase?.key || node?.baseKey);
  const canEdit = Boolean(!readOnly);
  const canOpenItem = typeof onRelatedNodeOpen === 'function';
  const columns = displayPrefs.columns || DEFAULT_PROPOSAL_ITEMS_DISPLAY_PREFS.columns;
  const showCoverPhoto = displayPrefs.showCoverPhoto !== false;
  const showDescription = displayPrefs.showDescription !== false;
  const showLineNumber = displayPrefs.showLineNumber !== false;

  const itemType = useMemo(() => getType(PROPOSAL_ITEM_TYPE_KEY), [getType]);
  const statusField = useMemo(() => getItemSchemaField(itemType, 'status'), [itemType]);
  const unitField = useMemo(() => getItemSchemaField(itemType, 'unit'), [itemType]);
  const itemTypeField = useMemo(() => getItemSchemaField(itemType, 'itemType'), [itemType]);
  const categoryField = useMemo(() => getItemSchemaField(itemType, 'category'), [itemType]);

  const enumOptions = useCallback((field) => {
    if (!field) return [];
    return resolveEnumOptionsForField(field, language).map((option) => ({
      value: String(option?.value ?? ''),
      label: getOptionLabel(option, language) || String(option?.value ?? ''),
    })).filter((option) => option.value);
  }, [language]);

  const unitOptions = useMemo(() => enumOptions(unitField), [enumOptions, unitField]);
  const itemTypeOptions = useMemo(() => enumOptions(itemTypeField), [enumOptions, itemTypeField]);
  const categoryOptions = useMemo(() => enumOptions(categoryField), [categoryField, enumOptions]);

  const showVendor = columns.vendor !== false;
  const showRoom = columns.room === true;

  const {
    options: vendorOptions,
    resolveOptionValue: resolveVendorOptionValue,
  } = useNodelistTargetOptions({
    targetType: 'VENDOR',
    baseKey,
    enabled: Boolean(baseKey && (showVendor || canEdit)),
  });
  const {
    options: roomOptions,
    resolveOptionValue: resolveRoomOptionValue,
  } = useNodelistTargetOptions({
    targetType: 'SPACE',
    baseKey,
    enabled: Boolean(baseKey && (showRoom || canEdit)),
  });

  useEffect(() => {
    setDisplayPrefs(readProposalItemsDisplayPrefs(baseKey));
  }, [baseKey]);

  const clientVisibleSerialized = JSON.stringify(
    node?.data_proposal?.clientVisibleColumns ?? null,
  );

  // Sync client-visible columns from the proposal document (by stored value, not any node churn).
  useEffect(() => {
    setClientVisibleColumns(
      normalizeProposalClientVisibleColumns(
        readProposalField(node, 'clientVisibleColumns', nodeType),
      ),
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed to serialized field + node identity
  }, [clientVisibleSerialized, node?.key, nodeType]);

  const items = useMemo(() => (
    (Array.isArray(neighborNodes) ? neighborNodes : [])
      .filter((row) => cleanString(row?.type).toUpperCase() === PROPOSAL_ITEM_TYPE_KEY)
      .sort((left, right) => {
        const leftOrder = Number(readProposalItemField(left, 'sortOrder'));
        const rightOrder = Number(readProposalItemField(right, 'sortOrder'));
        if (Number.isFinite(leftOrder) && Number.isFinite(rightOrder) && leftOrder !== rightOrder) {
          return leftOrder - rightOrder;
        }
        return cleanString(left?.title).localeCompare(cleanString(right?.title));
      })
  ), [neighborNodes]);

  const payments = useMemo(() => (
    (Array.isArray(neighborNodes) ? neighborNodes : [])
      .filter((row) => cleanString(row?.type).toUpperCase() === DOCUMENT_PAYMENT_TYPE_KEY)
  ), [neighborNodes]);

  useEffect(() => {
    const validKeys = new Set(
      items.map((item) => cleanString(item?.key)).filter(Boolean),
    );
    setSelectedKeys((prev) => {
      let changed = false;
      const next = new Set();
      prev.forEach((key) => {
        if (validKeys.has(key)) next.add(key);
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [items]);

  const rows = useMemo(
    () => items.map((item) => ({
      item,
      model: getProposalItemRowModel(item, itemType, language),
    })),
    [itemType, items, language],
  );

  const selectedCount = selectedKeys.size;
  const allSelected = rows.length > 0 && selectedCount === rows.length;
  const someSelected = selectedCount > 0;

  const rollups = useMemo(
    () => computeProposalItemsRollups(items, node, nodeType, payments),
    [items, node, nodeType, payments],
  );

  const handleItemChange = useCallback((itemKey, patch) => {
    if (!canEdit || !itemKey || !patch) return undefined;
    return updateNode(itemKey, patch);
  }, [canEdit]);

  const handleInlineCommit = useCallback(async (item, fieldName, nextValue, fieldType = 'string') => {
    if (!canEdit || !item?.key || !fieldName) return undefined;

    if (fieldName === 'title') {
      return updateNode(item.key, { title: cleanString(nextValue) });
    }
    if (fieldName === 'description') {
      return updateNode(item.key, buildNodeSubtitlePatch(nextValue));
    }

    const schemaField = getItemSchemaField(itemType, fieldName);
    const field = schemaField || {
      name: fieldName,
      source: 'proposal_item',
      type: fieldType,
    };

    let value = nextValue;
    if (fieldName === 'vendor' || fieldName === 'room') {
      const raw = cleanString(nextValue);
      if (!raw) {
        value = [];
      } else {
        try {
          const resolver = fieldName === 'vendor'
            ? resolveVendorOptionValue
            : resolveRoomOptionValue;
          const key = await resolver(raw);
          value = key ? [key] : [];
        } catch (error) {
          console.error('[ProposalItems] catalog adopt failed:', error);
          return undefined;
        }
      }
    }

    let patch = buildNodeFieldPatch(item, field, PROPOSAL_ITEM_TYPE_KEY, value);
    if (!patch) return undefined;

    patch = augmentProposalItemPricingPatch({
      node: item,
      nodeTypeKey: PROPOSAL_ITEM_TYPE_KEY,
      field,
      value,
      patch,
    }) || patch;

    return updateNode(item.key, patch);
  }, [canEdit, itemType, resolveRoomOptionValue, resolveVendorOptionValue]);

  const handleRowOpen = useCallback((item) => {
    if (!canOpenItem || !item?.key) return;
    onRelatedNodeOpen(item);
  }, [canOpenItem, onRelatedNodeOpen]);

  const handleDisplayPrefsSave = useCallback((nextPrefs) => {
    setDisplayPrefs(writeProposalItemsDisplayPrefs(baseKey, nextPrefs));
  }, [baseKey]);

  /**
   * Persists a single column's client visibility on the proposal document.
   *
   * @param {string} columnKey
   * @param {boolean} nextVisible
   * @returns {Promise<void>|undefined}
   */
  const handleClientVisibleChange = useCallback((columnKey, nextVisible) => {
    const key = cleanString(columnKey);
    if (!canEdit || !node?.key || !key) return undefined;
    if (!PROPOSAL_CLIENT_TOGGLEABLE_COLUMN_KEYS.includes(key)) return undefined;

    const next = normalizeProposalClientVisibleColumns({
      ...clientVisibleColumns,
      [key]: Boolean(nextVisible),
    });
    setClientVisibleColumns(next);

    const field = {
      name: 'clientVisibleColumns',
      source: PROPOSAL_TYPE_KEY,
    };
    const patch = buildNodeFieldPatch(node, field, PROPOSAL_TYPE_KEY, next);
    if (!patch) return undefined;
    return updateNode(node.key, patch);
  }, [canEdit, clientVisibleColumns, node]);

  const handleToggleRowSelected = useCallback((itemKey) => {
    const key = cleanString(itemKey);
    if (!key) return;
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const handleToggleSelectAll = useCallback(() => {
    setSelectedKeys((prev) => {
      if (rows.length > 0 && prev.size === rows.length) return new Set();
      return new Set(rows.map(({ model }) => cleanString(model.key)).filter(Boolean));
    });
  }, [rows]);

  const handleRemoveSelectedConfirm = useCallback(async () => {
    if (!canEdit || removeBusy || selectedKeys.size === 0) return;
    const keys = [...selectedKeys];
    setRemoveBusy(true);
    try {
      await Promise.all(
        keys.map((key) => moveNodeToTrash(key, {
          trashedByKey: user?.key || null,
        })),
      );
      setSelectedKeys(new Set());
      setRemoveConfirmOpen(false);
    } catch (error) {
      toast.error(error?.message || 'Could not remove the selected items.', {
        autoClose: 4000,
      });
    } finally {
      setRemoveBusy(false);
    }
  }, [canEdit, removeBusy, selectedKeys, user?.key]);

  if (!node) return null;

  const tableClassName = [
    'purchase-order-items-table',
    'proposal-items-table',
    showLineNumber ? '' : 'is-line-hidden',
    canEdit ? 'has-row-select' : '',
    canOpenItem ? 'has-row-actions' : '',
  ].filter(Boolean).join(' ');

  const removeConfirmMessage = selectedCount === 1
    ? 'Move this line item to Trash?'
    : `Move ${selectedCount} line items to Trash?`;

  return (
    <>
      <div className="purchase-order-items">
        <div className="purchase-order-items-header">
          <div className="purchase-order-items-title">
            {`Items (${rows.length})`}
          </div>
          <div className="purchase-order-items-header-actions">
            <IconButton
              iconKey="discover_tune"
              title="Customize items"
              size={30}
              iconSize={18}
              onClick={() => setSettingsOpen(true)}
            />
            {canEdit && someSelected ? (
              <Button
                label="Remove Items"
                iconKey="delete"
                height="30px"
                enabled={!removeBusy}
                onClick={() => setRemoveConfirmOpen(true)}
              />
            ) : null}
            {canEdit ? (
              <Button
                label="Add Items"
                iconKey="add"
                height="30px"
                onClick={() => setAddOpen(true)}
              />
            ) : null}
          </div>
        </div>

        <div className="purchase-order-items-body">
          {rows.length === 0 ? (
            <div className="purchase-order-items-empty">
              No items yet.
              {canEdit ? ' Add products from the library.' : ''}
            </div>
          ) : (
            <div className="purchase-order-items-scroll">
              <table className={tableClassName}>
                <thead>
                  <tr>
                    {canEdit ? (
                      <th className="purchase-order-items-col-select" scope="col">
                        <Checkbox
                          value={allSelected}
                          size={18}
                          onChange={handleToggleSelectAll}
                        />
                      </th>
                    ) : null}
                    {showLineNumber ? (
                      <th className="purchase-order-items-col-line" scope="col">#</th>
                    ) : null}
                    <th className="purchase-order-items-col-item">Item</th>
                    {columns.sku ? (
                      <DocumentItemsColumnHeader
                        label="SKU"
                        className="proposal-items-col-sku"
                        clientVisible={isProposalColumnClientVisible(clientVisibleColumns, 'sku')}
                        canToggleClientVisibility={canEdit}
                        onClientVisibleChange={(next) => handleClientVisibleChange('sku', next)}
                      />
                    ) : null}
                    {columns.itemType ? (
                      <DocumentItemsColumnHeader
                        label="Type"
                        clientVisible={isProposalColumnClientVisible(clientVisibleColumns, 'itemType')}
                        canToggleClientVisibility={canEdit}
                        onClientVisibleChange={(next) => handleClientVisibleChange('itemType', next)}
                      />
                    ) : null}
                    {columns.category ? (
                      <DocumentItemsColumnHeader
                        label="Category"
                        clientVisible={isProposalColumnClientVisible(clientVisibleColumns, 'category')}
                        canToggleClientVisibility={canEdit}
                        onClientVisibleChange={(next) => handleClientVisibleChange('category', next)}
                      />
                    ) : null}
                    {columns.status ? (
                      <DocumentItemsColumnHeader
                        label="Status"
                        className="purchase-order-items-col-status"
                        clientVisible={isProposalColumnClientVisible(clientVisibleColumns, 'status')}
                        canToggleClientVisibility={canEdit}
                        onClientVisibleChange={(next) => handleClientVisibleChange('status', next)}
                      />
                    ) : null}
                    {columns.manufacturer ? (
                      <DocumentItemsColumnHeader
                        label="Manufacturer"
                        clientVisible={isProposalColumnClientVisible(clientVisibleColumns, 'manufacturer')}
                        canToggleClientVisibility={canEdit}
                        onClientVisibleChange={(next) => handleClientVisibleChange('manufacturer', next)}
                      />
                    ) : null}
                    {columns.vendor ? (
                      <DocumentItemsColumnHeader
                        label="Vendor"
                        clientVisible={isProposalColumnClientVisible(clientVisibleColumns, 'vendor')}
                        canToggleClientVisibility={canEdit}
                        onClientVisibleChange={(next) => handleClientVisibleChange('vendor', next)}
                      />
                    ) : null}
                    <th className="purchase-order-items-col-qty">Qty</th>
                    <th className="purchase-order-items-col-unit">Unit</th>
                    {columns.cost ? (
                      <DocumentItemsColumnHeader
                        label="Cost"
                        className="proposal-items-col-money"
                        clientVisible={isProposalColumnClientVisible(clientVisibleColumns, 'cost')}
                        canToggleClientVisibility={canEdit}
                        onClientVisibleChange={(next) => handleClientVisibleChange('cost', next)}
                      />
                    ) : null}
                    {columns.msrp ? (
                      <DocumentItemsColumnHeader
                        label="MSRP"
                        className="proposal-items-col-money"
                        clientVisible={isProposalColumnClientVisible(clientVisibleColumns, 'msrp')}
                        canToggleClientVisibility={canEdit}
                        onClientVisibleChange={(next) => handleClientVisibleChange('msrp', next)}
                      />
                    ) : null}
                    {columns.markup ? (
                      <DocumentItemsColumnHeader
                        label="Markup"
                        className="proposal-items-col-markup"
                        clientVisible={isProposalColumnClientVisible(clientVisibleColumns, 'markup')}
                        canToggleClientVisibility={canEdit}
                        onClientVisibleChange={(next) => handleClientVisibleChange('markup', next)}
                      />
                    ) : null}
                    {columns.margin ? (
                      <DocumentItemsColumnHeader
                        label="Margin"
                        className="proposal-items-col-money"
                        clientVisible={isProposalColumnClientVisible(clientVisibleColumns, 'margin')}
                        canToggleClientVisibility={canEdit}
                        onClientVisibleChange={(next) => handleClientVisibleChange('margin', next)}
                      />
                    ) : null}
                    <th className="purchase-order-items-col-unit-price">Unit Price</th>
                    {columns.room ? (
                      <DocumentItemsColumnHeader
                        label="Room"
                        clientVisible={isProposalColumnClientVisible(clientVisibleColumns, 'room')}
                        canToggleClientVisibility={canEdit}
                        onClientVisibleChange={(next) => handleClientVisibleChange('room', next)}
                      />
                    ) : null}
                    {columns.color ? (
                      <DocumentItemsColumnHeader
                        label="Color"
                        clientVisible={isProposalColumnClientVisible(clientVisibleColumns, 'color')}
                        canToggleClientVisibility={canEdit}
                        onClientVisibleChange={(next) => handleClientVisibleChange('color', next)}
                      />
                    ) : null}
                    {columns.dimensions ? (
                      <DocumentItemsColumnHeader
                        label="Dimensions"
                        clientVisible={isProposalColumnClientVisible(clientVisibleColumns, 'dimensions')}
                        canToggleClientVisibility={canEdit}
                        onClientVisibleChange={(next) => handleClientVisibleChange('dimensions', next)}
                      />
                    ) : null}
                    {columns.shipping ? (
                      <DocumentItemsColumnHeader
                        label="Shipping"
                        className="proposal-items-col-money"
                        clientVisible={isProposalColumnClientVisible(clientVisibleColumns, 'shipping')}
                        canToggleClientVisibility={canEdit}
                        onClientVisibleChange={(next) => handleClientVisibleChange('shipping', next)}
                      />
                    ) : null}
                    {columns.shippingMarkup ? (
                      <DocumentItemsColumnHeader
                        label="Ship Markup"
                        clientVisible={isProposalColumnClientVisible(clientVisibleColumns, 'shippingMarkup')}
                        canToggleClientVisibility={canEdit}
                        onClientVisibleChange={(next) => handleClientVisibleChange('shippingMarkup', next)}
                      />
                    ) : null}
                    {columns.taxable ? (
                      <DocumentItemsColumnHeader
                        label="Taxable"
                        clientVisible={isProposalColumnClientVisible(clientVisibleColumns, 'taxable')}
                        canToggleClientVisibility={canEdit}
                        onClientVisibleChange={(next) => handleClientVisibleChange('taxable', next)}
                      />
                    ) : null}
                    {columns.website ? (
                      <DocumentItemsColumnHeader
                        label="Website"
                        clientVisible={isProposalColumnClientVisible(clientVisibleColumns, 'website')}
                        canToggleClientVisibility={canEdit}
                        onClientVisibleChange={(next) => handleClientVisibleChange('website', next)}
                      />
                    ) : null}
                    <th className="purchase-order-items-col-total">Total</th>
                    {canOpenItem ? (
                      <th className="purchase-order-items-col-actions" scope="col">
                        <span className="purchase-order-items-sr-only">Open</span>
                      </th>
                    ) : null}
                  </tr>
                </thead>
                <tbody>
                  {rows.map(({ item, model }, index) => (
                    <tr key={model.key}>
                      {canEdit ? (
                        <td className="purchase-order-items-col-select">
                          <Checkbox
                            value={selectedKeys.has(model.key)}
                            size={18}
                            onChange={() => handleToggleRowSelected(model.key)}
                          />
                        </td>
                      ) : null}
                      {showLineNumber ? (
                        <td className="purchase-order-items-col-line">{index + 1}</td>
                      ) : null}
                      <td className="purchase-order-items-col-item">
                        <div className="purchase-order-items-item-cell">
                          {showCoverPhoto ? (
                            <ItemThumb itemKey={model.key} coverUrl={model.coverUrl} />
                          ) : null}
                          <div className="purchase-order-items-item-text">
                            <InlineTextCell
                              value={model.title}
                              placeholder="Untitled"
                              canEdit={canEdit}
                              emphasized
                              onCommit={(nextValue) => handleInlineCommit(item, 'title', nextValue)}
                            />
                            {showDescription ? (
                              <InlineTextCell
                                value={canEdit ? model.description : model.subtitle}
                                placeholder={model.leadTime || 'Add description'}
                                canEdit={canEdit}
                                maxWidth={300}
                                onCommit={(nextValue) => (
                                  handleInlineCommit(item, 'description', nextValue)
                                )}
                              />
                            ) : null}
                          </div>
                        </div>
                      </td>
                      {columns.sku ? (
                        <td>
                          <InlineTextCell
                            value={model.sku}
                            placeholder="SKU"
                            canEdit={canEdit}
                            onCommit={(nextValue) => (
                              handleInlineCommit(item, 'sku', nextValue, 'string')
                            )}
                          />
                        </td>
                      ) : null}
                      {columns.itemType ? (
                        <td>
                          <InlineSelectCell
                            value={model.itemType}
                            options={itemTypeOptions}
                            placeholder="Type"
                            canEdit={canEdit && itemTypeOptions.length > 0}
                            onCommit={(nextValue) => (
                              handleInlineCommit(item, 'itemType', nextValue, 'enum')
                            )}
                          />
                        </td>
                      ) : null}
                      {columns.category ? (
                        <td>
                          <InlineSelectCell
                            value={model.category}
                            options={categoryOptions}
                            placeholder="Category"
                            canEdit={canEdit && categoryOptions.length > 0}
                            onCommit={(nextValue) => (
                              handleInlineCommit(item, 'category', nextValue, 'enum')
                            )}
                          />
                        </td>
                      ) : null}
                      {columns.status ? (
                        <td className="purchase-order-items-col-status">
                          <div className="purchase-order-items-status">
                            {statusField ? (
                              <EnumPillSelect
                                field={statusField}
                                value={model.status}
                                node={item}
                                typeKey={PROPOSAL_ITEM_TYPE_KEY}
                                canEdit={canEdit}
                                onNodeChange={handleItemChange}
                                placeholder="Status…"
                                language={language}
                                fontSize="12px"
                              />
                            ) : (
                              model.status || (
                                <span className="purchase-order-items-muted">—</span>
                              )
                            )}
                          </div>
                        </td>
                      ) : null}
                      {columns.manufacturer ? (
                        <td>
                          <InlineTextCell
                            value={model.manufacturer}
                            placeholder="Manufacturer"
                            canEdit={canEdit}
                            onCommit={(nextValue) => (
                              handleInlineCommit(item, 'manufacturer', nextValue, 'string')
                            )}
                          />
                        </td>
                      ) : null}
                      {columns.vendor ? (
                        <td>
                          <InlineSelectCell
                            value={model.vendorKey}
                            options={vendorOptions}
                            placeholder="Vendor"
                            canEdit={canEdit && vendorOptions.length > 0}
                            onCommit={(nextValue) => (
                              handleInlineCommit(item, 'vendor', nextValue, 'relation')
                            )}
                          />
                        </td>
                      ) : null}
                      <td className="purchase-order-items-col-qty">
                        <InlineNumberCell
                          value={model.quantity}
                          placeholder="1"
                          canEdit={canEdit}
                          onCommit={(nextValue) => (
                            handleInlineCommit(item, 'quantity', nextValue, 'number')
                          )}
                        />
                      </td>
                      <td className="purchase-order-items-col-unit">
                        <InlineSelectCell
                          value={model.unit}
                          options={unitOptions}
                          placeholder="Unit"
                          canEdit={canEdit && unitOptions.length > 0}
                          onCommit={(nextValue) => (
                            handleInlineCommit(item, 'unit', nextValue, 'enum')
                          )}
                        />
                      </td>
                      {columns.cost ? (
                        <td className="proposal-items-col-money">
                          <InlineMoneyCell
                            valueCents={model.cost}
                            canEdit={canEdit}
                            onCommit={(nextValue) => (
                              handleInlineCommit(item, 'cost', nextValue, 'money')
                            )}
                          />
                        </td>
                      ) : null}
                      {columns.msrp ? (
                        <td className="proposal-items-col-money">
                          <InlineMoneyCell
                            valueCents={model.msrp}
                            canEdit={canEdit}
                            onCommit={(nextValue) => (
                              handleInlineCommit(item, 'msrp', nextValue, 'money')
                            )}
                          />
                        </td>
                      ) : null}
                      {columns.markup ? (
                        <td className="proposal-items-col-markup">
                          {canEdit ? (
                            <InlineNumberCell
                              value={model.markupPercent}
                              placeholder="%"
                              canEdit={canEdit}
                              onCommit={(nextValue) => (
                                handleInlineCommit(item, 'markupPercent', nextValue, 'percentage')
                              )}
                            />
                          ) : (
                            formatMarkupCell(model.markupAmount, model.markupPercent)
                          )}
                        </td>
                      ) : null}
                      {columns.margin ? (
                        <td className="proposal-items-col-money">
                          {model.marginPercent == null
                            ? <span className="purchase-order-items-muted">—</span>
                            : `${model.marginPercent}%`}
                        </td>
                      ) : null}
                      <td className="purchase-order-items-col-unit-price">
                        <InlineMoneyCell
                          valueCents={model.unitPrice}
                          canEdit={canEdit}
                          onCommit={(nextValue) => (
                            handleInlineCommit(item, 'unitPrice', nextValue, 'money')
                          )}
                        />
                      </td>
                      {columns.room ? (
                        <td>
                          <InlineSelectCell
                            value={model.roomKey}
                            options={roomOptions}
                            placeholder="Room"
                            canEdit={canEdit && roomOptions.length > 0}
                            onCommit={(nextValue) => (
                              handleInlineCommit(item, 'room', nextValue, 'relation')
                            )}
                          />
                        </td>
                      ) : null}
                      {columns.color ? (
                        <td>
                          <InlineTextCell
                            value={model.color}
                            placeholder="Color"
                            canEdit={canEdit}
                            onCommit={(nextValue) => (
                              handleInlineCommit(item, 'color', nextValue, 'string')
                            )}
                          />
                        </td>
                      ) : null}
                      {columns.dimensions ? (
                        <td>
                          <InlineTextCell
                            value={model.dimensions}
                            placeholder="Dimensions"
                            canEdit={canEdit}
                            onCommit={(nextValue) => (
                              handleInlineCommit(item, 'dimensions', nextValue, 'string')
                            )}
                          />
                        </td>
                      ) : null}
                      {columns.shipping ? (
                        <td className="proposal-items-col-money">
                          <InlineMoneyCell
                            valueCents={model.shipping}
                            canEdit={canEdit}
                            onCommit={(nextValue) => (
                              handleInlineCommit(item, 'shipping', nextValue, 'money')
                            )}
                          />
                        </td>
                      ) : null}
                      {columns.shippingMarkup ? (
                        <td>
                          <InlineNumberCell
                            value={model.shippingMarkupPercent}
                            placeholder="%"
                            canEdit={canEdit}
                            onCommit={(nextValue) => (
                              handleInlineCommit(
                                item,
                                'shippingMarkupPercent',
                                nextValue,
                                'percentage',
                              )
                            )}
                          />
                        </td>
                      ) : null}
                      {columns.taxable ? (
                        <td className="proposal-items-col-taxable">
                          <Switch
                            checked={model.taxable}
                            disabled={!canEdit}
                            onChange={(nextValue) => (
                              handleInlineCommit(item, 'taxable', Boolean(nextValue), 'boolean')
                            )}
                          />
                        </td>
                      ) : null}
                      {columns.website ? (
                        <td>
                          <InlineTextCell
                            value={model.website}
                            placeholder="https://…"
                            canEdit={canEdit}
                            onCommit={(nextValue) => (
                              handleInlineCommit(item, 'website', nextValue, 'url')
                            )}
                          />
                        </td>
                      ) : null}
                      <td className="purchase-order-items-col-total">
                        {formatMoneyCell(model.total)}
                      </td>
                      {canOpenItem ? (
                        <td className="purchase-order-items-col-actions">
                          <div className="purchase-order-items-row-action">
                            <IconButton
                              iconKey="more_horiz"
                              title="Open item"
                              size={28}
                              iconSize={18}
                              onClick={() => handleRowOpen(item)}
                            />
                          </div>
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="purchase-order-items-footer">
            <div className="purchase-order-items-footer-row">
              <span className="purchase-order-items-footer-label">Subtotal</span>
              <span className="purchase-order-items-footer-value">
                {formatPrice(rollups.subTotal)}
              </span>
            </div>
            <div className="purchase-order-items-footer-row">
              <span className="purchase-order-items-footer-label">Tax</span>
              <span className="purchase-order-items-footer-value">
                {formatPrice(rollups.tax)}
              </span>
            </div>
            <hr className="purchase-order-items-footer-rule" />
            <div className="purchase-order-items-footer-row is-emphasis">
              <span className="purchase-order-items-footer-label">Total</span>
              <span className="purchase-order-items-footer-value">
                {formatPrice(rollups.total)}
              </span>
            </div>
            <div className="purchase-order-items-footer-row">
              <span className="purchase-order-items-footer-label">Amount Paid</span>
              <span className="purchase-order-items-footer-value">
                {formatPrice(rollups.amountPaid)}
              </span>
            </div>
            <div className="purchase-order-items-footer-row is-emphasis">
              <span className="purchase-order-items-footer-label">Balance Due</span>
              <span className="purchase-order-items-footer-value">
                {formatPrice(rollups.balanceDue)}
              </span>
            </div>
          </div>
        </div>
      </div>

      <AddProposalItemFromProductModal
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        proposalNode={node}
        baseKey={baseKey}
        user={user}
      />

      <ProposalItemsSettingsModal
        isOpen={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        prefs={displayPrefs}
        onSave={handleDisplayPrefsSave}
      />

      <Confirm
        title="Remove Items"
        message={removeConfirmMessage}
        confirmLabel={removeBusy ? 'Removing…' : 'Move to Trash'}
        isOpen={removeConfirmOpen}
        setOpen={setRemoveConfirmOpen}
        onConfirm={handleRemoveSelectedConfirm}
      />
    </>
  );
});

ProposalItems.displayName = 'ProposalItems';

export default ProposalItems;
